import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../components/LanguageContext';
import { ArrowRightIcon, Calendar } from 'lucide-react';
interface Props {
  onContinue: () => void;
  onSkipToApp?: () => void;
}
const SUMMIT_START = new Date('2026-05-08T09:00:00+02:00');
export function OnboardingSprout({
  onContinue,
  onSkipToApp
}: Props) {
  const [mounted, setMounted] = useState(false);
  const {
    t
  } = useLanguage();
  // Compute days until summit opens
  const computeDaysLeft = () => {
    const now = new Date();
    const ms = SUMMIT_START.getTime() - now.getTime();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  };
  const daysLeft = computeDaysLeft();
  // Countdown label resolution
  let countdownLabel: string | null = null;
  if (daysLeft > 1) {
    countdownLabel = t('welcome.countdown.days').replace('{n}', String(daysLeft));
  } else if (daysLeft === 1) {
    countdownLabel = t('welcome.countdown.oneDay');
  } else if (daysLeft === 0) {
    countdownLabel = t('welcome.countdown.today');
  } else if (daysLeft >= -2) {
    countdownLabel = t('welcome.countdown.live');
  }
  const videoRef = useCallback((node: HTMLVideoElement | null) => {
    if (node) {
      node.play().catch(() => {});
    }
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] relative overflow-hidden" data-id="element-793">
      {/* Full-screen background video */}
      <div className="absolute inset-0 w-full h-full z-0 pointer-events-none" style={{
      opacity: mounted ? 0.5 : 0,
      transition: 'opacity 2400ms cubic-bezier(0.4, 0, 0.2, 1)'
    }} data-id="element-794">
        <video ref={videoRef} autoPlay loop muted playsInline preload="auto"
      // @ts-ignore
      webkit-playsinline="" className="w-full h-full object-cover" src="https://cdn.magicpatterns.com/uploads/rWWEVJMg31E8rQ5Z5Vhyfi/file.mp4" data-id="element-795" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D]/60 via-transparent to-[#0D0D0D]/80" data-id="element-796" />
      </div>

      {/* Content overlay */}
      <div className="relative z-10 flex flex-col h-full" data-id="element-797">
        {/* Top bar: Partner Logos */}
        <div className="relative w-full" data-id="element-798">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D]/80 to-transparent pointer-events-none" data-id="element-799" />
          <div className="relative w-full flex items-center justify-between px-5 pt-4 pb-8" style={{
          opacity: mounted ? 1 : 0,
          transition: 'opacity 600ms ease-in-out',
          transitionDelay: '0ms'
        }} data-id="element-800">
            <img src="https://static.wixstatic.com/media/6e7739_d35a2f5e3bf74f039373a6840ce24def~mv2.png/v1/fill/w_228,h_88,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/6e7739_d35a2f5e3bf74f039373a6840ce24def~mv2.png" alt="EIIS" className="h-8 w-auto object-contain" data-id="element-801" />
            <img src="https://cdn.magicpatterns.com/uploads/ezyo28XQUrBbpPgBwVPXTt/Logo_Dmood.svg" alt="DMOOD" className="h-[15px] w-auto object-contain invert opacity-90" data-id="element-802" />
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" data-id="element-803" />

        {/* Bottom section: text + CTA */}
        <div className="relative px-6 pt-8 pb-8 flex flex-col items-center text-center" style={{
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(48px)',
        transition: 'opacity 600ms cubic-bezier(0.4, 0, 0.2, 1), transform 700ms cubic-bezier(0.16, 1, 0.3, 1)',
        transitionDelay: '300ms'
      }} data-id="element-804">
          {/* Countdown badge */}
          {countdownLabel && <div className="mb-4" style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(12px)',
          transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1), transform 500ms cubic-bezier(0.16, 1, 0.3, 1)',
          transitionDelay: '500ms'
        }} data-id="element-805">
              <button onClick={onSkipToApp} className="inline-flex items-center gap-1.5 bg-[#1A1A1A] border border-[#2A2A2A] rounded-full px-3 py-1.5 text-[#C5D82D] text-[12px] font-medium transition-all duration-150 hover:bg-[#2A2A2A] active:scale-[0.96] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5D82D]" data-id="element-806">
                <Calendar className="w-3.5 h-3.5" strokeWidth={2} data-id="element-807" />
                {countdownLabel}
              </button>
            </div>}

          {/* Skip to app link */}
          {onSkipToApp}

          {/* Main heading */}
          <h1 className="text-white text-[17px] font-semibold text-center whitespace-nowrap" style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(12px)',
          transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1), transform 500ms cubic-bezier(0.16, 1, 0.3, 1)',
          transitionDelay: '600ms'
        }} data-id="element-808">
            {t('welcome.heading')}
          </h1>

          {/* Summit wordmark image */}
          <div className="mt-3 mb-8 w-full flex justify-center" style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(12px)',
          transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1), transform 500ms cubic-bezier(0.16, 1, 0.3, 1)',
          transitionDelay: '750ms'
        }} data-id="element-809">
            <img src="https://static.wixstatic.com/media/4b542d_a3fc5396ebde465b9a70d13498436b59~mv2.png/v1/fill/w_886,h_488,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/Proposta%20visual-11.png" alt="European Innovation for Sustainability Summit" className="w-full object-contain" data-id="element-810" />
          </div>

          {/* Bottom CTA */}
          <div className="w-full" style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1), transform 500ms cubic-bezier(0.16, 1, 0.3, 1)',
          transitionDelay: '900ms'
        }} data-id="element-811">
            <button onClick={onContinue} className="w-full py-4 rounded-full border-[1.5px] border-[#C5D82D] text-[#C5D82D] bg-transparent text-[14px] font-semibold tracking-wide flex items-center justify-center gap-2 transition-all duration-150 hover:bg-[#C5D82D]/10 active:bg-[#C5D82D]/20 active:scale-[0.98] focus-visible:bg-[#C5D82D]/15 focus-visible:outline-none" data-id="element-812">
              {t('welcome.cta')}
              <ArrowRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-813" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}